import {useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect} from 'react';
import {useFormContext} from 'react-hook-form';
import ChekinProSettings from 'ChekinProSettings';
import {
  LocalStorage,
  SessionStorage,
  useResetFieldIfTypeChanged,
  Guest,
  isRequiredFormField,
  isActiveFormField,
  GUEST_FORM_NAMES,
} from '@chekin/core';
import {FieldsErrorResponse} from '@chekin/api';
import {usePreloadGuestData} from '../usePreloadGuestData';
import {useReservation} from 'context/reservation';
import {useBuildIVPayload, useGuestMutation} from 'hooks';
import {useOpenModals} from 'context/openModals';
import {AddFormType, useSchema} from 'context/guest';
import {
  IS_LEADER_GUEST_STORAGE_KEY,
  SHOWED_SUCCESS_REGISTER_COMPLETE_MODAL,
} from 'utils/constants';
import {FormTypes, getIsLeaderGuest} from 'utils/guestForm';
import {useIVStore, useErrorModal, StatusModalStates} from '@chekin/shared';
import {useGuestCard} from 'context/kiosko';
import {getPayloadDate} from 'utils/common';
import {buildGuestPayload} from './buildGuestPayload';
import {useErrorHandler} from 'components/GuestFormFields/useErrorHandler';
import {usePreloadPoliceData} from 'components/GuestFormFields/usePreloadPoliceData';
import {useMutateLivenessDetection} from 'hooks/useMutateLivenessDetection';
import {useDefaultFieldsMapper} from './useDefaultFieldsMapper';
import {useIsMounted, useModalControls} from '@chekin/guestapp-ui-kit';
import SignBlock from 'components/SignBlock';
import IncompleteRegisterModal from 'components/IncompleteRegisterModal';
import DiscardChangesModalSheet from 'components/ModalSheets/DiscardChanges';
import {GuestRegisteringModal} from 'components/GuestRegisteringModal';
import {GroupedFields} from './GroupedFields';
import {AgreementClausesBox} from 'components/AgreementClausesBox';

type UseResetCityIfCountryChangedProps = {
  cityFieldName: GUEST_FORM_NAMES;
  countryFieldName: GUEST_FORM_NAMES;
};
const useResetCityIfCountryChanged = ({
  cityFieldName,
  countryFieldName,
}: UseResetCityIfCountryChangedProps) => {
  const {watch, getValues, resetField} = useFormContext<FormTypes>();
  const countryValue = watch(countryFieldName)?.value;

  useEffect(() => {
    const city = getValues(cityFieldName);

    if (city?.value) {
      resetField(cityFieldName);
    }
  }, [countryValue, cityFieldName, resetField, getValues]);
};

type GuestFormFieldsProps = {
  housingCountry: string;
  guest?: Guest;
  onGuestRegistered?: (data: Guest) => Promise<unknown>;
};

function useGuestFormFields({
  guest,
  housingCountry,
  onGuestRegistered,
}: GuestFormFieldsProps) {
  const isMounted = useIsMounted();
  const guestId = guest?.id;
  const isEdition = Boolean(guestId);
  const {formState, watch, handleSubmit, getValues, setError, trigger, reset} =
    useFormContext<FormTypes>();

  const canRevalidateForm =
    formState.isSubmitted && !formState.isSubmitSuccessful && !formState.isSubmitting;

  const {setIsFormTouched} = useOpenModals();
  const {ErrorModal, displayError, closeErrorModal} = useErrorModal();
  const {data: reservation, reservationId} = useReservation();

  useEffect(() => {
    const isThereDirtyField = Boolean(Object.keys(formState.dirtyFields || {})?.length);
    setIsFormTouched(isThereDirtyField);
  }, [setIsFormTouched, formState]);

  const fullNameCurrentGuestRef = useRef('');
  const [guestRegistrationStatus, setGuestRegistrationStatus] = useState(
    StatusModalStates.IDLE,
  );
  const {resetAllIVState} = useIVStore();
  const {saveGuestCardData} = useGuestCard();

  const nationality = watch(GUEST_FORM_NAMES.nationality)?.value;
  const documentType = watch(GUEST_FORM_NAMES.document_type)?.value;
  const birthPlaceCountry = watch(GUEST_FORM_NAMES.birth_place_country)?.value;
  const arrivedFromCountry = watch(GUEST_FORM_NAMES.arrived_from_country)?.value;
  const nextDestinationCountry = watch(GUEST_FORM_NAMES.next_destination_country)?.value;
  const documentExpeditionCountry = watch(
    GUEST_FORM_NAMES.document_expedition_country,
  )?.value;
  const arrivedFromDistrict = watch(GUEST_FORM_NAMES.arrived_from_district)?.value;
  const residenceCountry = watch(GUEST_FORM_NAMES.residence_country)?.value;
  const residenceProvince = watch(GUEST_FORM_NAMES.residence_province)?.value;
  const citizenship = watch(GUEST_FORM_NAMES.citizenship)?.value;
  const birthDate = watch(GUEST_FORM_NAMES.birth_date);
  const invoiceLeaderType = watch(GUEST_FORM_NAMES.invoice_leader_type)?.value;
  const recipientCodeType = watch(GUEST_FORM_NAMES.recipient_code_type)?.value;

  const {
    formFields,
    setBirthCountry,
    setArrivalCountry,
    setArrivalDistrict,
    setNextDestinationCountry,
    setNationality,
    setDocumentExpeditionCountry,
    setResidenceCountry,
    setResidenceProvince,
    setCitizenship,
    setDocumentType,
    setBirthDate,
    setIsLeaderGuest,
    setInvoiceLeaderType,
    setRecipientCodeType,
    isGuestSchemaLoading,
    isGuestSchemaLoaded,
    isGuestSchemaFetching,
    agreementClauses,
  } = useSchema();

  const {clearPersistedStorageFormData} = usePreloadGuestData();
  const {preloadPoliceData} = usePreloadPoliceData({
    getValues,
    reset,
    disabled: isEdition,
  });

  useLayoutEffect(() => {
    preloadPoliceData();
  }, [preloadPoliceData]);

  const isLoadingForm = isGuestSchemaLoading && !isGuestSchemaLoaded;

  const isDisabledField = isGuestSchemaLoading || isGuestSchemaFetching;

  const closeGuestRegistrationStatusModal = useCallback(() => {
    setGuestRegistrationStatus(StatusModalStates.IDLE);
  }, []);

  const {
    isOpen: isDataIncompleteModalOpen,
    openModal: openDataIncompleteModalOpen,
    closeModal: closeDataIncompleteModalOpen,
  } = useModalControls();

  useEffect(() => {
    if (guest) {
      SessionStorage.set(IS_LEADER_GUEST_STORAGE_KEY, guest?.is_leader);
      setIsLeaderGuest?.(guest?.is_leader);
    } else {
      setIsLeaderGuest?.(getIsLeaderGuest());
    }
  }, [guest, setIsLeaderGuest]);

  useEffect(() => {
    setNationality?.(nationality);
  }, [nationality, setNationality]);
  useEffect(() => {
    setDocumentType?.(documentType);
  }, [documentType, setDocumentType]);
  useEffect(() => {
    setArrivalCountry?.(arrivedFromCountry);
  }, [arrivedFromCountry, setArrivalCountry]);
  useEffect(() => {
    setArrivalDistrict?.(arrivedFromDistrict);
  }, [arrivedFromDistrict, setArrivalDistrict]);
  useEffect(() => {
    setBirthCountry?.(birthPlaceCountry);
  }, [birthPlaceCountry, setBirthCountry]);
  useEffect(() => {
    setDocumentExpeditionCountry?.(documentExpeditionCountry);
  }, [documentExpeditionCountry, setDocumentExpeditionCountry]);
  useEffect(() => {
    setNextDestinationCountry?.(nextDestinationCountry);
  }, [nextDestinationCountry, setNextDestinationCountry]);
  useEffect(() => {
    setResidenceCountry?.(residenceCountry);
  }, [residenceCountry, setResidenceCountry]);
  useEffect(() => {
    setResidenceProvince?.(residenceProvince);
  }, [residenceProvince, setResidenceProvince]);
  useEffect(() => {
    setCitizenship?.(citizenship);
  }, [citizenship, setCitizenship]);
  useEffect(() => {
    const dateString = getPayloadDate(birthDate);
    setBirthDate?.(dateString);
  }, [birthDate, setBirthDate]);
  useEffect(() => {
    setInvoiceLeaderType?.(invoiceLeaderType);
  }, [invoiceLeaderType, setInvoiceLeaderType]);
  useEffect(() => {
    setRecipientCodeType?.(recipientCodeType);
  }, [recipientCodeType, setRecipientCodeType]);

  useEffect(
    function revalidateFormOnDisplayFieldsChange() {
      if (canRevalidateForm) {
        void trigger();
      }
    },
    [trigger, formFields, canRevalidateForm],
  );

  useResetFieldIfTypeChanged<FormTypes>({
    fieldName: GUEST_FORM_NAMES.birth_place_city,
    type: formFields?.[GUEST_FORM_NAMES.birth_place_city]?.type,
  });

  useResetFieldIfTypeChanged<FormTypes>({
    fieldName: GUEST_FORM_NAMES.arrived_from_municipality,
    type: formFields?.[GUEST_FORM_NAMES.arrived_from_municipality]?.type,
  });

  useResetCityIfCountryChanged({
    cityFieldName: GUEST_FORM_NAMES.birth_place_city,
    countryFieldName: GUEST_FORM_NAMES.birth_place_country,
  });

  useResetFieldIfTypeChanged<FormTypes>({
    fieldName: GUEST_FORM_NAMES.residence_city,
    type: formFields?.[GUEST_FORM_NAMES.residence_city]?.type,
  });
  useResetCityIfCountryChanged({
    cityFieldName: GUEST_FORM_NAMES.residence_city,
    countryFieldName: GUEST_FORM_NAMES.residence_country,
  });

  useResetFieldIfTypeChanged({
    fieldName: GUEST_FORM_NAMES.document_expedition_city,
    type: formFields?.[GUEST_FORM_NAMES.document_expedition_city]?.type,
  });
  useResetCityIfCountryChanged({
    cityFieldName: GUEST_FORM_NAMES.document_expedition_city,
    countryFieldName: GUEST_FORM_NAMES.document_expedition_country,
  });

  const isRegisteredModalOpen = guestRegistrationStatus !== StatusModalStates.IDLE;
  const closeRegisteredModal = useCallback(() => {
    resetAllIVState();
    closeGuestRegistrationStatusModal();
  }, [closeGuestRegistrationStatusModal, resetAllIVState]);

  const {mutate: mutateLivenessResultData} = useMutateLivenessDetection({});
  const livenessResultData = useIVStore(state => state.livenessResultData);
  const isIvFailed = useIVStore(state => state.isIvFailed);

  const {GuestErrorModal, displayFieldErrors} = useErrorHandler({
    setError,
    fields: formFields,
    onDataIncomplete: openDataIncompleteModalOpen,
  });

  const handleError = (error: FieldsErrorResponse) => {
    if (!isMounted.current) {
      return;
    }

    if (error.status_code !== 400) {
      closeGuestRegistrationStatusModal();
      displayError(error);
      return;
    }

    if ((error as FieldsErrorResponse).errors) {
      displayFieldErrors(error as FieldsErrorResponse);
    } else {
      displayFieldErrors(error);
    }
    closeGuestRegistrationStatusModal();
  };

  const guestMutation = useGuestMutation({
    onSuccess: async (data: Guest) => {
      saveGuestCardData(data);

      await onGuestRegistered?.(data);

      setGuestRegistrationStatus(StatusModalStates.SUCCESS);
      ChekinProSettings.events.onGuestRegistered(data);

      if (livenessResultData) {
        mutateLivenessResultData({
          reservation_id: reservationId ?? '',
          is_detection_passed: livenessResultData?.passed ?? false,
          detected_head_movements: livenessResultData?.completedActions ?? [],
          liveness_video_link: livenessResultData.livenessVideoUrl,
          performed_by: livenessResultData.performedBy,
          guest_id: data?.id ?? '',
        });
      }
      clearPersistedStorageFormData();
    },
    onError: handleError,
  });

  const {getIVPayload} = useBuildIVPayload();

  const onSubmit = useCallback(
    async (formData: FormTypes, signature: string, guestId?: string) => {
      closeErrorModal();

      fullNameCurrentGuestRef.current = `${formData[GUEST_FORM_NAMES.name]} ${
        formData[GUEST_FORM_NAMES.surname]
      }`;
      setGuestRegistrationStatus(StatusModalStates.LOADING);

      const identityVerificationPayload = getIVPayload();
      const isLeader = getIsLeaderGuest();
      const extraFormData = {
        is_leader: isLeader,
        is_iv_failed: isIvFailed,
      };
      const payload = buildGuestPayload({
        signature,
        data: {
          ...formData,
          ...extraFormData,
        },
        reservationId: reservationId,
        identityVerificationPayload,
        formFields,
      });

      if (guestMutation && reservationId) {
        guestMutation.mutate({
          id: guestId,
          payload,
        });
      }

      LocalStorage.remove(SHOWED_SUCCESS_REGISTER_COMPLETE_MODAL);
    },
    [closeErrorModal, getIVPayload, reservationId, guestMutation, formFields, isIvFailed],
  );

  const handleSubmitForm = useCallback(
    (signature: string) => {
      handleSubmit(
        (data: FormTypes) => onSubmit(data, signature, guestId),
        openDataIncompleteModalOpen,
      )();
    },
    [onSubmit, handleSubmit, guestId, openDataIncompleteModalOpen],
  );

  const defaultFieldsMapper = useDefaultFieldsMapper({
    guestId,
    isDisabledField,
    housingCountry,
  });

  const fieldsGroupVariant =
    ChekinProSettings.appVariant === 'airbnb' ? 'airbnb' : 'default';

  const renderFormFields = () => (
    <GroupedFields
      defaultFieldsMapper={defaultFieldsMapper}
      formFields={formFields as AddFormType}
      variant={fieldsGroupVariant}
    />
  );

  const hasSignature = useMemo(() => {
    return isActiveFormField(formFields?.[GUEST_FORM_NAMES.signature]);
  }, [formFields]);

  const SignatureField = useMemo(() => {
    return (
      <>
        <GuestRegisteringModal
          open={isRegisteredModalOpen}
          name={fullNameCurrentGuestRef.current}
          onClose={closeRegisteredModal}
          state={guestRegistrationStatus}
          isEdition={isEdition}
        />

        <SignBlock
          agreementClauses={<AgreementClausesBox clauses={agreementClauses} />}
          onSubmit={handleSubmitForm}
          automatedSignature={reservation?.auto_signature}
          hasSignature={hasSignature}
          isSignatureRequired={isRequiredFormField(
            formFields?.[GUEST_FORM_NAMES.signature],
          )}
          openDataIncompleteModalOpen={openDataIncompleteModalOpen}
        />
      </>
    );
  }, [
    isRegisteredModalOpen,
    guestRegistrationStatus,
    closeRegisteredModal,
    isEdition,
    handleSubmitForm,
    reservation?.auto_signature,
    hasSignature,
    formFields,
    openDataIncompleteModalOpen,
    agreementClauses,
  ]);

  const FormModals = useCallback(() => {
    return (
      <>
        <IncompleteRegisterModal
          open={isDataIncompleteModalOpen}
          onClose={closeDataIncompleteModalOpen}
        />
        <DiscardChangesModalSheet />
        <ErrorModal />
        <GuestErrorModal />
      </>
    );
  }, [
    isDataIncompleteModalOpen,
    closeDataIncompleteModalOpen,
    ErrorModal,
    GuestErrorModal,
  ]);

  return {
    isLoadingForm,
    renderFormFields,
    FormModals,
    SignatureField,
    hasSignature,
    isDataIncompleteModalOpen,
    isLoadedForm: isGuestSchemaLoaded,
  };
}

export {useGuestFormFields};
